import React, {FC, useState, useEffect, useRef} from "react";
import {
  Container
} from './styles'
import { useQueryParam, StringParam, NumberParam } from 'use-query-params';
import { Table, TableSkeleton, TopProgress, Typography, Button, ModalVariables } from "../../../components";
import { openstackPlanService } from "../../../api";
import { timeConverterMMDDYYHHMM } from "../../../utils/dates";
// import { StatusText } from "overview/styles";
import { HeadLine1 } from "../../../components/typography/styled";
import { useTranslation } from "react-i18next";
import { formatCurrency } from "../../../utils/display-price";
import Skeleton from 'react-loading-skeleton'
import { useTheme } from "styled-components";
import { handleStatusName } from "../../../utils/handle-status-name";
import { icons } from "../../../icons";
import { useNavigate } from "react-router-dom";
import { useUi } from "../../../context/ui.context";
import { extractErrorMessage } from "../../../utils/error-extractor";

const OpenStackPlans:FC = () => {
  const theme:any = useTheme()
  const [page, setPage] = useQueryParam('page', NumberParam);
  const [ordering, setOrdering] = useQueryParam('ordering', StringParam);
  const [pageSize, setPageSize] = useQueryParam('page_size', NumberParam)
  const [paginationData, setPaginationData] = useState<any>()
  const [invoices, setInvoices] = useState<any[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [pageLoading, setPageLoading] = useState<boolean>(true)
  const [viewModal, setViewModal] = useState(false)
  const [activeAnimation, setActiveAnimation] = useState(false)
  const [selectedPlan, setSelectedPlan] = useState<any>()
  const [deleteLoading, setDeleteLoading] = useState(false)
  const settingDefaultPlanRef = useRef(false)
  const [t] = useTranslation()
  const { viewAlert } = useUi()
  const navigate = useNavigate()
  const getInvoices = () => {
    setLoading(true)
    openstackPlanService.getOpenstackPlans({
      page:page || 1,
      page_size:pageSize || 20,
      // ordering:ordering || '-issue_date',
    })
    .then((res) => {
      setInvoices(res.data.objects?.map((obj:any) => {
        return{
          ...obj,
          navigate:`/settings/openstack-plans/plan/${obj.id}`,
          pricing_rules_count:obj.pricing_rules?.length || 0,
          is_default_display:obj.is_default ? t("YES") : t("NO"),
        }
      }))
      setPaginationData(res.data)
      setPageLoading(false)
      setLoading(false)
    })
    .catch((e) => {
      console.log('error', e);
    })
  }
  useEffect(() => {
    getInvoices()
    const interval = setInterval(() => {
      getInvoices()
    }, 5000)
    return () => {
      clearInterval(interval)
    }
  },[page,
    ordering,
    pageSize,])

  const closeDeleteModal = () => {
    setViewModal(false)
    setActiveAnimation(false)
    setSelectedPlan(undefined)
  }

  const deletePlan = async () => {
    if (!selectedPlan) return

    try {
      setDeleteLoading(true)
      const res = await openstackPlanService.deletePlanById(selectedPlan.id)
      viewAlert({
        severity: 'info',
        message: res?.data?.detail || t("PRICING_PLAN_DELETED"),
      })
      closeDeleteModal()
      getInvoices()
    } catch (e: any) {
      viewAlert({
        severity: 'error',
        message: e?.response?.data?.detail || t("ERROR_OCCURRED"),
      })
    } finally {
      setDeleteLoading(false)
    }
  }

  const setPlanAsDefault = async (plan:any) => {
    if (settingDefaultPlanRef.current) return

    try {
      settingDefaultPlanRef.current = true
      const res = await openstackPlanService.setPlanAsDefault(plan.id, plan.name)
      viewAlert({
        severity: 'info',
        message: res?.data?.detail || t("PRICING_PLAN_SET_AS_DEFAULT"),
      })
      getInvoices()
    } catch (e:any) {
      viewAlert({
        severity: 'error',
        message:
          e?.response?.data?.detail ||
          extractErrorMessage(e?.response?.data) ||
          t("ERROR_OCCURRED"),
      })
    } finally {
      settingDefaultPlanRef.current = false
    }
  }

  const ColumnsInvoices = [
    {
      title:t("NAME"),
      key:'name'
    },
    {
      title:t("SERVICES_COUNT"),
      key:'services_count',
    },
    {
      title:t("PRICING_RULES_COUNT"),
      key:'pricing_rules_count',
    },
    {
      title:t("CURRENCY"),
      key:'currency',
    },
    {
      title:t("IS_DEFAULT"),
      key:'is_default_display',
    },
    {
      title:'',
      key:'props'
    }
  ]
  const contextMenu = [
    {
      title:t("CREATE_PRICING_RULE"),
      id:'1',
      icon:icons.PlusDefault,
      onClick: (item:any) => {
        navigate(`/settings/openstack-plans/plan/${item.id}/create-rule`)
      },
      divider:false
    },
    {
      title:t("EDIT"),
      id:'2',
      icon:icons.Edit,
      onClick: (item:any) => {
        navigate(`/settings/openstack-plans/plan/${item.id}/edit`)
      },
      divider:false
    },
    {
      title:t("SET_PRICING_PLAN_AS_DEFAULT"),
      id:'3',
      icon:icons.CheckMark,
      onClick: (item:any) => setPlanAsDefault(item),
      hidden: (item:any) => item.is_default,
      divider:false
    },
    {
      title:t("DELETE"),
      id:'4',
      icon:icons.DeleteIcon,
      onClick: (item:any) => {
        setSelectedPlan(item)
        setViewModal(true)
        setActiveAnimation(true)
      },
      divider:false
    }
  ]
  if(pageLoading) return (
    <Container>
      <TopProgress loading={pageLoading}/>
      <Skeleton
        baseColor={theme.palette[300]}
        highlightColor={theme.palette[20]}
        width={125} style={{marginBottom:"5px"}} height={30}
      />
      <TableSkeleton
        rows={10}
        columns={6}
        // columnsData={Columns}
      />
    </Container>
  )
  return (
    <Container>
      <div style={{display:'flex', gap:'10px', alignItems:'center', justifyContent:'space-between', marginBottom:'10px'}}>
        <HeadLine1>
          {t("OPENSTACK_PLANS")}
        </HeadLine1>
        <Button
          title={t("CREATE_PLAN") || "Create Plan"}
          onClick={() => {
            navigate(`/settings/openstack-plans/plan/create`)
          }}
          variant='primary'
          size='display'
        />
      </div>
      {invoices?.length > 0 ?
        <Table
          columns={ColumnsInvoices}
          rows={invoices}
          // hideOther
          hideCheckbox
          settings={contextMenu}
          paginationData={paginationData}
        />
      :
        <Typography variant="body">
          {t("NO_OPENSTACK_PLANS")}
        </Typography>
      }
      <ModalVariables
        modalType="delete"
        closeModalCancel={closeDeleteModal}
        viewModal={viewModal}
        activeAnimation={activeAnimation}
        closeModal={deletePlan}
        alertTitle={t("PRICING_PLAN")}
        alertSubTitle={selectedPlan ? [selectedPlan] : []}
        loading={deleteLoading}
      />
    </Container>
  )
}

export default OpenStackPlans
